/* eslint-disable @typescript-eslint/no-explicit-any */
//Import Mixpanel SDK
import mixpanel from "mixpanel-browser";
import {
  MIXPANEL_LOGGING_ENABLED,
  MIXPANEL_SESSION_RECORDING,
  MIXPANEL_TOKEN,
} from "@/constants/api";
import type { OverridedMixpanel } from "mixpanel-browser";

interface MixpanelWithWorkspace extends OverridedMixpanel {
  setCurrentWorkspaceName: (workspaceName: string) => void;
  __old_track: (
    eventName: string,
    properties?: Record<string, any>,
    ...args: any[]
  ) => void;
}

const noOp = (opName: string) => {
  return (...args: any[]) => {
    if (MIXPANEL_LOGGING_ENABLED) {
      const serializedArgs = args
        .map((arg) => {
          if (typeof arg === "function") return "[Function]";
          try {
            return JSON.stringify(arg);
          } catch {
            return String(arg);
          }
        })
        .join(", ");
      console.warn(
        `Mixpanel ${opName} called with (${serializedArgs}) but Mixpanel is not initialized.`,
      );
    }
  };
};

if (MIXPANEL_LOGGING_ENABLED) {
  console.log("Mixpanel logging is enabled.");
}

const log = (message: string, data?: any) => {
  if (MIXPANEL_LOGGING_ENABLED) {
    console.log(message, data);
  }
};

let mixpanelExport = mixpanel as MixpanelWithWorkspace;
let currentWorkspaceName = "unknown workspace";

if (MIXPANEL_TOKEN) {
  // Create an instance of the Mixpanel object, your token is already added to this snippet
  mixpanelExport.init(MIXPANEL_TOKEN, {
    autocapture: false,
    record_sessions_percent: MIXPANEL_SESSION_RECORDING,
  });
  mixpanelExport.__old_track = mixpanelExport.track;
  mixpanelExport.track = function (
    eventName: string,
    properties?: Record<string, any>,
    ...args: any[]
  ) {
    const enhancedProperties = {
      ...(properties || {}),
      workspace: currentWorkspaceName,
      currentPath: window.location.pathname,
    };
    log(`Mixpanel track called with event: ${eventName}`, enhancedProperties);
    mixpanelExport.__old_track(eventName, enhancedProperties, ...args);
  };
  mixpanelExport.setCurrentWorkspaceName = (workspaceName: string) => {
    currentWorkspaceName = workspaceName;
    log(`Mixpanel current workspace set to: ${workspaceName}`);
  };
  log("Mixpanel initialized");
} else {
  mixpanelExport = {
    track: noOp("track"),
    __old_track: noOp("__old_track"),
    identify: noOp("identify"),
    people: {
      set: noOp("people.set"),
      set_once: noOp("people.set_once"),
      unset: noOp("people.unset"),
      increment: noOp("people.increment"),
      remove: noOp("people.remove"),
      append: noOp("people.append"),
      union: noOp("people.union"),
      delete_user: noOp("people.delete_user"),
    },
    setCurrentWorkspaceName: (workspaceName: string) => {
      currentWorkspaceName = workspaceName;
      log(
        `Mixpanel setCurrentWorkspaceName called with: ${workspaceName} but Mixpanel is not initialized.`,
      );
    },
  } as unknown as MixpanelWithWorkspace;
  console.warn("Mixpanel token not found. Mixpanel tracking is disabled.");
}

export default mixpanelExport;
